// src/components/Cockpit/MemoryHeatMap.tsx
// ============================================
// MemoryHeatMap - AI 学习可视化（Cockpit 2.0 / Sprint 3 Task 3）
//
// 设计理念：让 AI 的学习过程"可见"
//
// 展示 5 种记忆类型的实时热度：
//   - Conversation（对话记忆）
//   - Knowledge（知识记忆）
//   - Preference（偏好记忆）
//   - Semantic（语义记忆）
//   - Reflection（反思记忆）
//
// 视觉：
//   - 7×24 热力图（7 天 × 24 小时），格子颜色深度表示该时段记忆写入次数
//   - 右侧 5 种记忆类型的实时计数 + 趋势条
//   - 顶部：今日学习总量 + 反思次数
//   - 鼠标悬浮显示具体数值
// ============================================

import { motion } from "framer-motion";
import { Brain, MessageCircle, BookMarked, Heart, Sparkles, RotateCw } from "lucide-react";

// ============================================
// 类型定义
// ============================================

export type MemoryType =
  | "conversation"
  | "knowledge"
  | "preference"
  | "semantic"
  | "reflection";

export interface MemoryTypeMeta {
  type: MemoryType;
  label: string;
  icon: typeof Brain;
  color: string;
  /** 当前总量 */
  count: number;
  /** 今日新增 */
  todayDelta: number;
  /** 7 天趋势（每天一个值） */
  trend: number[];
}

export interface HeatCell {
  /** 0-6 周一到周日 */
  day: number;
  /** 0-23 小时 */
  hour: number;
  /** 该时段记忆写入次数 */
  value: number;
}

// ============================================
// 默认数据
// ============================================

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export const DEFAULT_MEMORY_TYPES: MemoryTypeMeta[] = [
  {
    type: "conversation",
    label: "Conversation",
    icon: MessageCircle,
    color: "#38BDF8",
    count: 1284,
    todayDelta: 42,
    trend: [30, 45, 38, 52, 41, 48, 42],
  },
  {
    type: "knowledge",
    label: "Knowledge",
    icon: BookMarked,
    color: "#34D399",
    count: 856,
    todayDelta: 18,
    trend: [12, 15, 22, 18, 25, 20, 18],
  },
  {
    type: "preference",
    label: "Preference",
    icon: Heart,
    color: "#EC4899",
    count: 124,
    todayDelta: 3,
    trend: [2, 4, 3, 5, 4, 3, 3],
  },
  {
    type: "semantic",
    label: "Semantic",
    icon: Sparkles,
    color: "#A78BFA",
    count: 432,
    todayDelta: 12,
    trend: [8, 10, 9, 14, 11, 13, 12],
  },
  {
    type: "reflection",
    label: "Reflection",
    icon: RotateCw,
    color: "#FBBF24",
    count: 68,
    todayDelta: 2,
    trend: [1, 2, 1, 3, 2, 2, 2],
  },
];

/** 生成 7×24 热力图模拟数据 */
function generateHeatMap(): HeatCell[] {
  const cells: HeatCell[] = [];
  for (let day = 0; day < 7; day++) {
    for (let hour = 0; hour < 24; hour++) {
      // 工作日白天热度高，夜间低；周末整体偏低
      const isWeekday = day < 5;
      const isWorkHour = hour >= 9 && hour <= 22;
      let base = isWeekday ? (isWorkHour ? 8 : 2) : 3;
      // 早晚高峰
      if (hour === 9 || hour === 14 || hour === 20) base += 6;
      if (hour === 22 || hour === 23) base = Math.max(1, base - 4);
      // 加点随机
      const value = Math.max(0, base + Math.floor(Math.random() * 4) - 2);
      cells.push({ day, hour, value });
    }
  }
  return cells;
}

export const DEFAULT_HEATMAP: HeatCell[] = generateHeatMap();

// ============================================
// 颜色映射（value → 背景）
// ============================================

function heatColor(value: number): string {
  if (value === 0) return "rgba(255,255,255,0.02)";
  if (value <= 2) return "rgba(56,189,248,0.15)";
  if (value <= 5) return "rgba(56,189,248,0.35)";
  if (value <= 8) return "rgba(52,211,153,0.45)";
  if (value <= 12) return "rgba(251,191,36,0.55)";
  return "rgba(236,72,153,0.65)";
}

// ============================================
// 单个热力格子
// ============================================

function HeatCellView({
  cell,
  delay,
}: {
  cell: HeatCell;
  delay: number;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.5 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3, delay, ease: "easeOut" }}
      whileHover={{ scale: 1.4, zIndex: 10 }}
      className="rounded-sm relative group"
      style={{
        background: heatColor(cell.value),
        boxShadow:
          cell.value > 8 ? `0 0 8px ${heatColor(cell.value)}` : "none",
      }}
      title={`${DAYS[cell.day]} ${String(cell.hour).padStart(2, "0")}:00 · ${cell.value} writes`}
    >
      {/* 悬浮提示 */}
      <div className="absolute z-20 bottom-full left-1/2 -translate-x-1/2 mb-1 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap">
        <div className="bg-[#0B1220] border border-white/10 rounded px-1.5 py-0.5 text-[9px] font-mono text-white">
          {DAYS[cell.day]} {String(cell.hour).padStart(2, "0")}:00 · {cell.value}
        </div>
      </div>
    </motion.div>
  );
}

// ============================================
// 记忆类型条目
// ============================================

function MemoryTypeRow({
  meta,
  index,
}: {
  meta: MemoryTypeMeta;
  index: number;
}) {
  const Icon = meta.icon;
  const maxTrend = Math.max(...meta.trend, 1);

  return (
    <motion.div
      initial={{ opacity: 0, x: -8 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.3, delay: 0.2 + index * 0.06 }}
      className="relative flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-white/[0.04] transition-colors"
    >
      <div
        className="flex-shrink-0 w-6 h-6 rounded-md flex items-center justify-center"
        style={{
          background: `${meta.color}15`,
          border: `1px solid ${meta.color}30`,
        }}
      >
        <Icon size={12} style={{ color: meta.color }} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between">
          <span className="text-[10px] font-mono text-gray-300 truncate">
            {meta.label}
          </span>
          <span
            className="text-[11px] font-mono font-bold"
            style={{ color: meta.color }}
          >
            {meta.count.toLocaleString()}
          </span>
        </div>
        {/* 7 天趋势条 */}
        <div className="flex items-end gap-0.5 mt-1 h-3">
          {meta.trend.map((v, i) => (
            <div
              key={i}
              className="flex-1 rounded-sm"
              style={{
                height: `${(v / maxTrend) * 100}%`,
                background: `${meta.color}${v === meta.todayDelta ? "80" : "40"}`,
                minHeight: 2,
              }}
            />
          ))}
        </div>
      </div>
      <div className="text-right">
        <div className="text-[10px] font-mono text-emerald-400">
          +{meta.todayDelta}
        </div>
        <div className="text-[8px] font-mono text-gray-600 uppercase">today</div>
      </div>
    </motion.div>
  );
}

// ============================================
// MemoryHeatMap 容器
// ============================================

interface MemoryHeatMapProps {
  types?: MemoryTypeMeta[];
  cells?: HeatCell[];
}

export default function MemoryHeatMap({
  types = DEFAULT_MEMORY_TYPES,
  cells = DEFAULT_HEATMAP,
}: MemoryHeatMapProps) {
  const todayTotal = types.reduce((acc, t) => acc + t.todayDelta, 0);
  const totalCount = types.reduce((acc, t) => acc + t.count, 0);
  const reflectionToday = types.find((t) => t.type === "reflection")?.todayDelta || 0;

  return (
    <div className="relative rounded-xl border border-white/[0.06] bg-white/[0.02] backdrop-blur-md p-4 h-full flex flex-col overflow-hidden">
      {/* 顶部光带 */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-purple-500/40 to-transparent" />

      {/* 头部 */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-1 h-4 bg-gradient-to-b from-purple-400 to-purple-600 rounded-full" />
          <h3 className="text-sm font-medium text-white">Memory HeatMap</h3>
          <span className="text-[10px] font-mono text-gray-500 uppercase tracking-wider">
            AI Learning
          </span>
        </div>
        <div className="flex items-center gap-3 text-[10px] font-mono">
          <span className="flex items-center gap-1">
            <Brain size={11} className="text-purple-400" />
            <span className="text-purple-400 font-bold">{totalCount.toLocaleString()}</span>
            <span className="text-gray-600">total</span>
          </span>
          <span className="flex items-center gap-1">
            <motion.span
              className="w-1.5 h-1.5 rounded-full bg-emerald-400"
              animate={{ opacity: [1, 0.3, 1] }}
              transition={{ duration: 1.5, repeat: Infinity }}
            />
            <span className="text-emerald-400 font-bold">+{todayTotal}</span>
            <span className="text-gray-600">today</span>
          </span>
        </div>
      </div>

      <div className="flex-1 grid grid-cols-12 gap-3 min-h-0">
        {/* 左侧：7×24 热力图 */}
        <div className="col-span-8 flex flex-col min-h-0">
          <div className="flex items-center justify-between mb-1">
            <span className="text-[9px] font-mono uppercase tracking-wider text-gray-500">
              7 Days × 24 Hours
            </span>
            <span className="text-[9px] font-mono text-gray-600">
              {reflectionToday} reflections today
            </span>
          </div>
          {/* 热力图主体 */}
          <div className="flex-1 flex gap-1 overflow-hidden">
            {/* 星期标签列 */}
            <div className="flex flex-col gap-0.5 justify-around py-1">
              {DAYS.map((d) => (
                <span
                  key={d}
                  className="text-[8px] font-mono text-gray-600 text-right pr-1"
                >
                  {d}
                </span>
              ))}
            </div>
            {/* 格子网格：7 行 × 24 列 */}
            <div className="flex-1 grid grid-rows-7 gap-0.5">
              {DAYS.map((_, dayIdx) => (
                <div key={dayIdx} className="grid grid-cols-24 gap-0.5">
                  {cells
                    .filter((c) => c.day === dayIdx)
                    .map((cell, i) => (
                      <HeatCellView
                        key={`${cell.day}-${cell.hour}`}
                        cell={cell}
                        delay={0.05 + (cell.day * 24 + cell.hour) * 0.002}
                      />
                    ))}
                </div>
              ))}
            </div>
          </div>
          {/* 底部小时标签 */}
          <div className="flex justify-between mt-1 pl-7 pr-1 text-[8px] font-mono text-gray-600">
            <span>00</span>
            <span>06</span>
            <span>12</span>
            <span>18</span>
            <span>23</span>
          </div>
        </div>

        {/* 右侧：5 种记忆类型 */}
        <div className="col-span-4 flex flex-col gap-1 overflow-y-auto">
          {types.map((meta, idx) => (
            <MemoryTypeRow key={meta.type} meta={meta} index={idx} />
          ))}
        </div>
      </div>
    </div>
  );
}
